// TradingJournal.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Goal, Skull, Calendar, TrendingUp, TrendingDown, Target, ChevronLeft, ChevronRight, Info } from 'lucide-react';
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from 'recharts';
import { useChartWindow } from '../../hooks/useChartWindow';
import './TradingJournal.css';

// ── 차트 상태별 dot 색상 ──────────────────────────────
const STATUS_DOT_COLOR = {
  BUY:         '#22c55e',
  SELL:        '#ef4444',
  HOLDING:     '#3b82f6',
  NO_POSITION: '#d1d5db',
};

const ScoreDot = ({ cx, cy, payload }) => {
  if (cx == null || cy == null) return null;
  const color = STATUS_DOT_COLOR[payload?.status] || '#d1d5db';
  return <circle cx={cx} cy={cy} r={3.5} fill={color} stroke="#fff" strokeWidth={1.5} />;
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const data = payload[0]?.payload;
  if (!data) return null;
  const hasPrice = payload.some(p => p.dataKey === 'priceN');
  const hasScore = payload.some(p => p.dataKey === 'scoreN');
  return (
    <div className="journal-chart-tooltip">
      <p className="journal-chart-tooltip-date">{label}</p>
      {hasPrice && data.price != null && (
        <p className="journal-chart-tooltip-price">
          ${data.price?.toLocaleString()}
        </p>
      )}
      {hasScore && data.score != null && (
        <p className="journal-chart-tooltip-score">
          AI 점수 {data.score > 0 ? '+' : ''}{data.score}
        </p>
      )}
    </div>
  );
};

// 윈도우 인터랙션 상수
const DEFAULT_WINDOW = 20;
const INTERACTIVE_MIN = 30; // 데이터가 이 이하면 전체 표시 + 인터랙션 비활성

// ── JournalChart ───────────────────────────────────────
// AI 점수 라인 스타일 옵션
const SCORE_STYLES = [
  { id: 'dashedDot', label: '점선+점', dash: '5 3', showDot: true },
  { id: 'dashed',    label: '점선',    dash: '5 3', showDot: false },
  { id: 'solid',     label: '선',      dash: '0',   showDot: false },
];

const JournalChart = ({ scoreChartData }) => {
  const [scoreStyleId, setScoreStyleId] = useState('dashedDot');
  const scoreStyle = SCORE_STYLES.find(s => s.id === scoreStyleId) || SCORE_STYLES[0];

  const filteredData = useMemo(() => (
    (scoreChartData || [])
      .filter(item => item.scoreDate && item.stockPrice)
      .map(item => ({
        date: item.scoreDate,
        price: parseFloat(item.stockPrice),
        score: item.score ?? 0,
        status: item.status,
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date))
  ), [scoreChartData]);

  const isInteractive = filteredData.length > INTERACTIVE_MIN;
  const { viewWindow, chartRef, bindings, isInteracting } = useChartWindow({
    dataLength: filteredData.length,
    defaultWindow: DEFAULT_WINDOW,
    isInteractive,
  });

  const visibleData = useMemo(() => {
    if (filteredData.length === 0) return [];
    return filteredData.slice(viewWindow.start, viewWindow.end + 1);
  }, [filteredData, viewWindow]);

  // 윈도우 내 min-max 정규화 → 두 시리즈를 0~100 공통 스케일에 올려 상관관계 가시화
  const normRanges = useMemo(() => {
    if (visibleData.length === 0) return null;
    const prices = visibleData.map(d => d.price).filter(v => v != null && !isNaN(v));
    const scores = visibleData.map(d => d.score).filter(v => v != null && !isNaN(v));
    return {
      pMin: prices.length ? Math.min(...prices) : 0,
      pMax: prices.length ? Math.max(...prices) : 1,
      sMin: scores.length ? Math.min(...scores) : -1,
      sMax: scores.length ? Math.max(...scores) : 1,
    };
  }, [visibleData]);

  const chartData = useMemo(() => {
    if (!normRanges) return [];
    const { pMin, pMax, sMin, sMax } = normRanges;
    const pSpan = pMax - pMin || 1;
    const sSpan = sMax - sMin || 1;
    return visibleData.map(d => ({
      ...d,
      priceN: d.price != null && !isNaN(d.price) ? ((d.price - pMin) / pSpan) * 100 : null,
      scoreN: d.score != null && !isNaN(d.score) ? ((d.score - sMin) / sSpan) * 100 : null,
    }));
  }, [visibleData, normRanges]);

  // 정규화 좌표(0~100)를 시리즈별 실제 값 라벨로 환원
  const formatPriceTick = useCallback((v) => {
    if (!normRanges) return '';
    const { pMin, pMax } = normRanges;
    const raw = pMin + (v / 100) * (pMax - pMin);
    if (Math.abs(raw) >= 1000) return `$${(raw / 1000).toFixed(1)}k`;
    return `$${raw.toFixed(raw < 10 ? 2 : 0)}`;
  }, [normRanges]);

  const formatScoreTick = useCallback((v) => {
    if (!normRanges) return '';
    const { sMin, sMax } = normRanges;
    const raw = sMin + (v / 100) * (sMax - sMin);
    const decimals = Math.abs(sMax - sMin) < 5 ? 1 : 0;
    const sign = raw > 0 ? '+' : '';
    return `${sign}${raw.toFixed(decimals)}`;
  }, [normRanges]);

  if (!filteredData.length) return null;

  return (
    <div className="journal-chart-wrap">
      <div className="journal-chart-header">
        <div className="journal-chart-legend">
          <span className="legend-item">
            <span className="legend-price-line" />
            종가
          </span>
          <span className="legend-item">
            <span className="legend-score-line" />
            AI 점수
          </span>
        </div>
        <div className="score-style-picker" role="radiogroup" aria-label="AI 점수 라인 스타일">
          {SCORE_STYLES.map((s) => (
            <button
              key={s.id}
              role="radio"
              aria-checked={scoreStyleId === s.id}
              className={`score-style-btn${scoreStyleId === s.id ? ' score-style-btn--active' : ''}`}
              onClick={() => setScoreStyleId(s.id)}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* 차트 영역 (큰 데이터셋에서 swipe/pinch/wheel 인터랙션 — useChartWindow) */}
      <div
        ref={chartRef}
        className={isInteractive ? 'journal-chart-interactive' : undefined}
        {...bindings}
      >
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={chartData} margin={{ top: 8, right: 4, left: 0, bottom: 8 }}>
            <defs>
              <linearGradient id="priceGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor="#3b82f6" stopOpacity={0.15} />
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
            <XAxis
              dataKey="date"
              tickFormatter={d => d.slice(5)}
              tick={{ fontSize: 10, fill: '#9ca3af' }}
              axisLine={false}
              tickLine={false}
              interval="preserveStartEnd"
              tickMargin={10}
            />
            {/* 시각은 0~100 정규화 동일 축, 라벨만 시리즈별 실제값으로 색상 구분.
                padding으로 위/아래 여백 확보 → 최고/최저 라인이 차트 끝선에 닿지 않도록 */}
            <YAxis
              yAxisId="price"
              orientation="left"
              domain={[0, 100]}
              ticks={[0, 25, 50, 75, 100]}
              tick={{ fontSize: 10, fill: '#3b82f6', fontWeight: 600 }}
              axisLine={false}
              tickLine={false}
              width={52}
              tickMargin={8}
              padding={{ top: 10, bottom: 10 }}
              tickFormatter={formatPriceTick}
            />
            <YAxis
              yAxisId="score"
              orientation="right"
              domain={[0, 100]}
              ticks={[0, 25, 50, 75, 100]}
              tick={{ fontSize: 10, fill: '#8B7BC7', fontWeight: 600 }}
              axisLine={false}
              tickLine={false}
              width={40}
              tickMargin={8}
              padding={{ top: 10, bottom: 10 }}
              tickFormatter={formatScoreTick}
            />
            <Tooltip
              cursor={!isInteracting}
              content={(props) => (isInteracting ? null : <ChartTooltip {...props} />)}
            />
            <Area
              yAxisId="price"
              type="monotone"
              dataKey="priceN"
              stroke="#3b82f6"
              strokeWidth={2}
              fill="url(#priceGrad)"
              dot={false}
              activeDot={{ r: 4, fill: '#3b82f6' }}
              isAnimationActive={false}
            />
            <Line
              yAxisId="score"
              type="monotone"
              dataKey="scoreN"
              stroke="#B8A7E8"
              strokeWidth={1.5}
              strokeDasharray={scoreStyle.dash}
              dot={scoreStyle.showDot ? <ScoreDot /> : false}
              activeDot={{ r: 4, fill: '#B8A7E8' }}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export const TradingJournal = ({ stockName, scoreHistory = [], scoreChartData = [], simulationResults = [], monitoringDayCount = 0, totalProfit = 0}) => {
  const [showTooltip, setShowTooltip] = useState(null);
  // Transform scoreHistory data to journal format
  const transformScoreHistoryToJournal = (scoreHistory) => {
    if (!scoreHistory || !Array.isArray(scoreHistory)) {
      console.warn('scoreHistory is not a valid array:', scoreHistory);
      return [];
    }

    const transformed = scoreHistory.map((item, index) => {
      let actual = 'neutral';
      let actualPercent = 0;
      
      // Check if required fields exist
      if (!item.scoreDate) {
        console.warn('Missing scoreDate in item:', item);
        return null;
      }
      
      if (item.afterCloseStockPrice && item.beforeCloseStockPrice) {
        const priceChange = item.afterCloseStockPrice - item.beforeCloseStockPrice;
        actualPercent = ((priceChange / item.beforeCloseStockPrice) * 100);
        actual = priceChange > 0 ? 'up' : priceChange < 0 ? 'down' : 'neutral';
      } else {
        console.warn('Missing stock price data in item:', item);
      }

      // Determine if prediction was correct based on score and actual movement
      const isCorrect = (item.score >= 0 && actual === 'up') || (item.score < 0 && actual === 'down');

      const result = {
        date: item.scoreDate.split('T')[0], // Extract date from ISO string
        actual: actual,
        actualPercent: Math.round(actualPercent * 100) / 100, // Round to 2 decimal places
        prediction: item.score || 0,
        isCorrect: isCorrect,
        status: item.status, // Add status from API
        aiModelId: item.aiModelId || null,
      };
      
      return result;
    })
    .filter(item => item !== null)
    //.filter(item => Math.abs(item.prediction) >= 70); // 예측 점수가 70 이상인 것만
    
    const filtered = transformed.filter(item => item.actual !== 'neutral');

    // Sort by date in descending order (newest first)
    return filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  // Use state to handle data updates
  const [journalData, setJournalData] = useState([
  ]);

  // Update journal data when scoreHistory changes
  useEffect(() => {
    if (scoreHistory && scoreHistory.length > 0) {
      const transformedData = transformScoreHistoryToJournal(scoreHistory);
      if (transformedData.length > 0) {
        setJournalData(transformedData);
      } else {
        console.warn('Transformed data is empty, keeping fallback data');
      }
    }
  }, [scoreHistory]);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const totalPages = Math.ceil(journalData.length / itemsPerPage);

  // stockName이 변경될 때 currentPage를 1로 초기화
  useEffect(() => {
    setCurrentPage(1);
  }, [stockName]);

  const getCurrentPageData = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return journalData.slice(startIndex, endIndex);
  };

  const formatDateLine1 = (dateString) => {
    const date = new Date(dateString);
    const yy = date.getFullYear().toString().slice(2);
    const mm = date.getMonth() + 1;
    return `'${yy}년 ${mm}월`;
  };

  const formatDateLine2 = (dateString) => {
    const date = new Date(dateString);
    const dd = date.getDate();
    const weekday = date.toLocaleDateString('ko-KR', { weekday: 'short' });
    return `${dd}일 (${weekday})`;
  };

  const getActualStatusIcon = (actual) => {
    return actual === 'up' ? (
      <TrendingUp size={13} className="text-green-500" />
    ) : (
      <TrendingDown size={13} className="text-red-500" />
    );
  };

  const getActualStatusText = (actual) => {
    return actual === 'up' ? '상승' : '하락';
  };

  const getActualStatusColor = (actual) => {
    return actual === 'up' ? 'text-green-500' : 'text-red-500';
  };

  const getPredictionColor = (prediction) => {
    if (prediction > 0) return 'text-green-500';
    if (prediction < 0) return 'text-red-500';
    return 'text-gray-500';
  };

  const getAccuracyColor = (isCorrect) => {
    return isCorrect ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600';
  };

  const getAIModelLogo = (aiModelId) => {
    if (!aiModelId) return null;
    const lower = aiModelId.toLowerCase();
    if (lower.includes('gemini')) return '/LLMLogo/gemini.svg';
    if (lower.includes('gpt') || lower.includes('openai')) return '/LLMLogo/gpt.svg';
    return null;
  };

  const getStatusDisplay = (status) => {
    switch(status) {
      case 'SELL': return { text: '매도', color: 'text-red-600', badge: 'bg-red-50 text-red-600 border border-red-100' };
      case 'BUY': return { text: '매수', color: 'text-green-600', badge: 'bg-green-50 text-green-600 border border-green-100' };
      case 'HOLDING': return { text: '보유', color: 'text-blue-600', badge: 'bg-blue-50 text-blue-600 border border-blue-100' };
      case 'NO_POSITION': return { text: '미보유', color: 'text-gray-500', badge: 'bg-gray-50 text-gray-500 border border-gray-100' };
      default: return { text: '미보유', color: 'text-gray-500', badge: 'bg-gray-50 text-gray-500 border border-gray-100' };
    }
  };

  const getRowBackgroundColor = (status) => {
    switch(status) {
      case 'SELL': return 'bg-red-50/50 hover:bg-red-50/80';
      case 'BUY': return 'bg-green-50/50 hover:bg-green-50/80';
      case 'HOLDING': return 'bg-blue-50/40 hover:bg-blue-50/70';
      case 'NO_POSITION': return 'hover:bg-gray-50/60';
      default: return 'hover:bg-gray-50/60';
    }
  };

  // 페이지네이션 번호 생성 함수 (축약형)
  const generatePageNumbers = (currentPage, totalPages) => {
    const pages = [];
    
    if (totalPages <= 7) {
      // 페이지가 7개 이하면 모든 페이지 표시
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // 페이지가 많을 때 축약형 적용
      if (currentPage <= 4) {
        // 현재 페이지가 앞쪽에 있을 때: 1 2 3 4 5 ... 11
        pages.push(1, 2, 3, 4, 5, '...', totalPages);
      } else if (currentPage >= totalPages - 3) {
        // 현재 페이지가 뒤쪽에 있을 때: 1 ... 7 8 9 10 11
        pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        // 현재 페이지가 중간에 있을 때: 1 ... 4 5 6 ... 11
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }
    
    return pages;
  };

  return (
    <div>

      {/* 통계 요약 */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="journal-stat-card bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-1.5 mb-2.5">
            <Calendar size={13} className="text-gray-400" />
            <p className="text-xs font-medium text-gray-400">거래 추적일</p>
          </div>
          <p className="text-2xl font-bold text-gray-900 leading-none tracking-tight">
            {monitoringDayCount}
            <span className="text-sm font-semibold text-gray-400 ml-1">일</span>
          </p>
        </div>

        <div className={`journal-stat-card rounded-xl p-4 border shadow-sm ${totalProfit >= 0 ? 'bg-blue-50 border-blue-100' : 'bg-red-50 border-red-100'}`}>
          <div className="flex items-center gap-1.5 mb-2.5">
            <p className={`text-xs font-medium ${totalProfit >= 0 ? 'text-blue-400' : 'text-red-400'}`}>수익률</p>
            <button
              className="relative"
              onMouseEnter={() => setShowTooltip('simulation')}
              onMouseLeave={() => setShowTooltip(null)}
            >
              <Info size={10} className={`hover:opacity-80 ${totalProfit >= 0 ? 'text-blue-300' : 'text-red-300'}`} />
              {showTooltip === 'simulation' && (
                <div className="absolute bottom-full right-0 mb-2 w-48 p-3 bg-gray-800 text-white text-xs rounded-lg shadow-xl z-10">
                  <div className="mb-1.5 font-semibold">시뮬레이션 수익률이란?</div>
                  <div className="text-gray-300 leading-relaxed">
                    AI 점수 70점 이상 매수, -70 이하 매도 기준 누적 수익률입니다.
                  </div>
                </div>
              )}
            </button>
          </div>
          <p className={`text-2xl font-bold leading-none tracking-tight ${totalProfit >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
            {totalProfit
              ? `${totalProfit > 0 ? '+' : ''}${(totalProfit * 100).toFixed(2)}%`
              : '-%'
            }
          </p>
        </div>
      </div>


      {/* 차트 */}
      <JournalChart scoreChartData={scoreChartData} />

      {/* 테이블 */}
      <div className="journal-table bg-white">
        {/* 테이블 헤더 */}
        <div className="bg-gray-50/80 px-5 py-2.5 border-b border-gray-100">
          <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-gray-400 uppercase tracking-widest">
            <div className="col-span-3 text-left">날짜</div>
            <div className="col-span-3 text-center">실제주가</div>
            <div className="col-span-2">점수</div>
            <div className="col-span-2 text-center">모델</div>
            <div className="col-span-2 relative flex items-center gap-1 whitespace-nowrap">
              상태
              <button
                className="relative"
                onMouseEnter={() => setShowTooltip('status')}
                onMouseLeave={() => setShowTooltip(null)}
              >
                <Info size={10} className="text-gray-400 hover:text-gray-600" />
                {showTooltip === 'status' && (
                  <div className="absolute top-full right-0 mt-1 w-52 p-3 bg-gray-800 text-white text-xs rounded-lg shadow-xl z-50 whitespace-normal">
                    <div className="mb-1.5 font-semibold">상태 기준</div>
                    <div className="text-gray-300 leading-relaxed space-y-1">
                      <div><span className="text-blue-300 font-semibold">보유</span> — AI 점수 70점 이상 매수 후 매도 전까지</div>
                      <div><span className="text-gray-400 font-semibold">미보유</span> — AI 점수 -70 이하 매도 후 다음 매수 전까지</div>
                    </div>
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* 테이블 바디 */}
        <div>
          {getCurrentPageData().map((item, index) => (
            <div
              key={item.date}
              className={`journal-row px-5 py-3.5 border-b border-gray-50 last:border-0 transition-colors duration-150 ${getRowBackgroundColor(item.status)}`}
              style={{ animationDelay: `${index * 45}ms` }}
            >
              <div className="grid grid-cols-12 gap-2 items-center">
                {/* 날짜 */}
                <div className="col-span-3">
                  <p className="text-xs font-semibold text-gray-800 leading-none">{formatDateLine1(item.date)}</p>
                  <p className="text-xs text-gray-400 mt-1">{formatDateLine2(item.date)}</p>
                </div>

                {/* 실제 주가 */}
                <div className="col-span-3 flex flex-col items-center justify-center">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center ${item.actual === 'up' ? 'bg-green-50' : 'bg-red-50'}`}>
                    {getActualStatusIcon(item.actual)}
                  </div>
                  <p className={`text-xs font-semibold mt-1 leading-none ${getActualStatusColor(item.actual)}`}>
                    {item.actualPercent > 0 ? '+' : ''}{item.actualPercent}%
                  </p>
                </div>

                {/* AI 예측 점수 */}
                <div className="col-span-2 flex items-center">
                  <span className={`text-xs font-bold leading-none ${getPredictionColor(item.prediction)}`}>
                    {item.prediction > 0 ? '+' : ''}{item.prediction}
                  </span>
                </div>

                {/* 모델 */}
                <div className="col-span-2 flex items-center justify-center">
                  {getAIModelLogo(item.aiModelId) && (
                    <img
                      src={getAIModelLogo(item.aiModelId)}
                      alt={item.aiModelId}
                      className="w-4 h-4 object-contain block"
                    />
                  )}
                </div>

                {/* 상태 배지 */}
                <div className="col-span-2 flex items-center">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap leading-none ${getStatusDisplay(item.status).badge}`}>
                    {getStatusDisplay(item.status).text}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1 mt-5">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="journal-pagination-btn"
          >
            <ChevronLeft size={14} />
          </button>

          <div className="flex gap-0.5">
            {generatePageNumbers(currentPage, totalPages).map((page, index) => {
              if (page === '...') {
                return (
                  <span
                    key={`ellipsis-${index}`}
                    className="w-8 h-8 flex items-center justify-center text-xs text-gray-300"
                  >
                    ···
                  </span>
                );
              }
              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`journal-page-num ${currentPage === page ? 'active' : ''}`}
                >
                  {page}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="journal-pagination-btn"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
};

// TradingJournalMobile.js - 모바일용 카드 스타일
const TradingJournalMobile = () => {
  const [journalData] = useState([
    
  ]);

  return (
    <div className="feature-section">
      <div className="feature-icon">
        <Calendar size={24} />
      </div>
      <h2 className="text-xl font-bold mb-2 text-black">매매일지</h2>
      <p className="text-gray-600 mb-6">
        실제 주가 움직임과 AI 예측을 비교하여 투자 성과를 분석하고 개선점을 찾아보세요.
      </p>

      <div className="space-y-4">
        {journalData.map((item, index) => (
          <div key={item.date} className="bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <Calendar size={16} className="text-gray-400" />
                <span className="text-sm font-medium text-gray-900">{item.date}</span>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full font-medium ${item.isCorrect ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                }`}>
                {item.isCorrect ? '정확' : '불일치'}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500 mb-1">실제 주가</p>
                <div className="flex items-center space-x-2">
                  {item.actual === 'up' ? (
                    <TrendingUp size={16} className="text-green-500" />
                  ) : (
                    <TrendingDown size={16} className="text-red-500" />
                  )}
                  <span className={`text-sm font-semibold ${item.actual === 'up' ? 'text-green-500' : 'text-red-500'
                    }`}>
                    {item.actual === 'up' ? '상승' : '하락'} ({item.actualPercent > 0 ? '+' : ''}{item.actualPercent}%)
                  </span>
                </div>
              </div>

              <div>
                <p className="text-xs text-gray-500 mb-1">AI 예측</p>
                <div className="flex items-center space-x-2">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${item.prediction > 0 ? 'bg-green-500' : item.prediction < 0 ? 'bg-red-500' : 'bg-gray-500'
                    }`}>
                    {Math.abs(item.prediction)}
                  </div>
                  <span className={`text-sm font-semibold ${item.prediction > 0 ? 'text-green-500' : item.prediction < 0 ? 'text-red-500' : 'text-gray-500'
                    }`}>
                    {item.prediction > 0 ? '+' : ''}{item.prediction}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TradingJournal;

